// ─────────────────────────────────────────────────────────────
// SongInfoView — edits the current project's ST metadata.
// Reads auto-fill values from the project engine.
// Saves everything to profile meta (persists with Cmd+S, export, cloud backup).
// ─────────────────────────────────────────────────────────────

import {createElement} from "@opendaw/lib-jsx"
import {Lifecycle} from "@opendaw/lib-std"
import {StudioService} from "@/service/StudioService.ts"
import {STApi} from "../STApi"
import {STCategory, STProjectMeta, STType} from "../STTypes"
import {CloudflareConfig} from "../../cloudflare/CloudflareConfig"
import {ClerkAuth} from "../../auth/ClerkAuth"

type Construct = {lifecycle: Lifecycle; service: StudioService}

// ── Helpers ───────────────────────────────────────────────────

const readST = (service: StudioService): STProjectMeta => {
    if (!service.hasProfile) return {}
    const m = service.profile.meta as STProjectMeta & Record<string, unknown>
    return {
        st_type:               m.st_type as STType | undefined,
        st_categories:         m.st_categories as STCategory[] | undefined ?? [],
        st_isPreviewable:      m.st_isPreviewable as boolean | undefined ?? false,
        st_is_premium:         m.st_is_premium   as boolean | undefined ?? false,
        st_db_id:              m.st_db_id as string | undefined,
        st_status:             (m.st_status as STProjectMeta["st_status"]) ?? "draft",
        st_key:                m.st_key as number | undefined,
        st_octaveUp:           m.st_octaveUp as number[] | undefined ?? [],
        st_octaveDown:         m.st_octaveDown as number[] | undefined ?? [],
        st_tags:               m.st_tags as string[] | undefined ?? [],
        st_playerType:         m.st_playerType as STProjectMeta["st_playerType"] | undefined ?? "song",
        st_preacherCompanion:  m.st_preacherCompanion as STProjectMeta["st_preacherCompanion"] | undefined,
        st_image_url:          m.st_image_url  as string | undefined,
        st_image_data:         m.st_image_data as string | undefined,
        st_isHidden:           m.st_isHidden             as boolean | undefined ?? false,
        st_isPreachModeFeatured: m.st_isPreachModeFeatured as boolean | undefined ?? false,
    }
}

const writeST = (service: StudioService, patch: Partial<STProjectMeta>): void => {
    if (!service.hasProfile) return
    for (const [key, value] of Object.entries(patch)) {
        ;(service.profile.meta as Record<string, unknown>)[key] = value
    }
    service.profile.updateModifyDate()
}

// ── Toggle component ──────────────────────────────────────────

const Toggle = ({value, onChange}: {value: boolean; onChange: (v: boolean) => void}) => {
    let current = value
    const thumb: HTMLElement = <div className="st-toggle-thumb"/>
    const el: HTMLElement = (
        <div className={`st-toggle${current ? " on" : ""}`}>
            {thumb}
        </div>
    )
    el.addEventListener("click", () => {
        current = !current
        el.classList.toggle("on", current)
        onChange(current)
    })
    return el
}

// ── StatusPill — draft / published / archived switcher ────────

type SongStatus = "draft" | "published" | "archived"

const STATUS_OPTIONS: {value: SongStatus; label: string; color: string}[] = [
    {value: "draft",     label: "Draft",     color: "#888888"},
    {value: "published", label: "Published", color: "#4caf50"},
    {value: "archived",  label: "Archived",  color: "#e07c3a"},
]

const StatusPill = ({value, onChange}: {value: SongStatus; onChange: (v: SongStatus) => void}) => {
    let current = value

    const pills = STATUS_OPTIONS.map(opt => {
        const pill: HTMLElement = (
            <button
                type="button"
                className={`st-status-pill${current === opt.value ? " active" : ""}`}
                style={{
                    "--pill-color": opt.color,
                } as unknown as CSSStyleDeclaration & Record<string, string>}>
                {opt.label}
            </button>
        )
        pill.addEventListener("click", () => {
            current = opt.value
            pills.forEach((p, i) => p.classList.toggle("active", STATUS_OPTIONS[i].value === current))
            onChange(current)
        })
        return pill
    })

    const wrap: HTMLElement = <div className="st-status-pill-row">{...pills}</div>
    return wrap
}

// ── Combobox — searchable dropdown with inline create ─────────

type ComboItem = {id: string; name: string; color: string}

const Combobox = ({
    placeholder,
    items,
    onSelect,
    onCreate,
}: {
    placeholder: string
    items: () => ComboItem[]
    onSelect: (item: ComboItem) => void
    onCreate: (name: string) => ComboItem
}) => {
    let query = ""
    let dropdownVisible = false
    const input: HTMLInputElement = (
        <input className="st-input" type="text" placeholder={placeholder}/>
    )
    const dropdown: HTMLElement = <div className="st-combo-dropdown" style={{display: "none"}}/>
    const wrap: HTMLElement = (
        <div className="st-combo-wrap">
            {input}
            {dropdown}
        </div>
    )

    const buildDropdown = () => {
        dropdown.innerHTML = ""
        const filtered = items().filter(i => i.name.toLowerCase().includes(query.toLowerCase()))
        for (const item of filtered) {
            const opt: HTMLElement = <div className="st-combo-option">{item.name}</div>
            opt.addEventListener("click", () => {
                onSelect(item)
                input.value = ""
                query = ""
                hideDropdown()
            })
            dropdown.appendChild(opt)
        }
        if (query.trim() && !items().some(i => i.name.toLowerCase() === query.toLowerCase())) {
            const createOpt: HTMLElement = (
                <div className="st-combo-option create">+ Create "{query.trim()}"</div>
            )
            createOpt.addEventListener("click", () => {
                const newItem = onCreate(query.trim())
                onSelect(newItem)
                input.value = ""
                query = ""
                hideDropdown()
            })
            dropdown.appendChild(createOpt)
        }
    }

    const showDropdown = () => {
        dropdownVisible = true
        buildDropdown()
        dropdown.style.display = "block"
    }
    const hideDropdown = () => {
        dropdownVisible = false
        dropdown.style.display = "none"
    }

    input.addEventListener("focus", showDropdown)
    input.addEventListener("input", () => {
        query = input.value
        buildDropdown()
        if (!dropdownVisible) showDropdown()
    })
    input.addEventListener("blur", () => setTimeout(hideDropdown, 150))

    return wrap
}

// ── Key map (matches Base44 KeypadSelector exactly) ───────────

const KEY_MAP = [
    {value: 0, label: "C"},
    {value: 1, label: "C#"},
    {value: 2, label: "D"},
    {value: 3, label: "Eb"},
    {value: 4, label: "E"},
    {value: 5, label: "F"},
    {value: 6, label: "F#"},
    {value: 7, label: "G"},
    {value: 8, label: "Ab"},
    {value: 9, label: "A"},
    {value: 10, label: "Bb"},
    {value: 11, label: "B"},
]

// ── KeySelect — single key dropdown (undefined = not set) ─────

const KeySelect = ({value, onChange}: {value: number | undefined; onChange: (v: number | undefined) => void}) => {
    const sel: HTMLSelectElement = (
        <select className="st-input">
            <option value="">— Select key —</option>
            {KEY_MAP.map(k => <option value={String(k.value)}>{k.label}</option>)}
        </select>
    )
    sel.value = value != null ? String(value) : ""
    sel.addEventListener("change", () => {
        const v = sel.value === "" ? undefined : Number(sel.value)
        onChange(v)
    })
    return sel
}

// ── KeyArraySelect — single dropdown that stores value as number[] ─
// Selecting a key stores [n]; selecting "None" stores [].
// This satisfies the Expo app which expects number[] for octaveUp/octaveDown.

const KeyArraySelect = ({value, onChange}: {value: number[]; onChange: (v: number[]) => void}) => {
    const current = value[0] ?? null
    const sel: HTMLSelectElement = (
        <select className="st-input">
            <option value="">— None —</option>
            {KEY_MAP.map(k => <option value={String(k.value)}>{k.label}</option>)}
        </select>
    )
    sel.value = current != null ? String(current) : ""
    sel.addEventListener("change", () => {
        const v = sel.value === "" ? [] : [Number(sel.value)]
        onChange(v)
    })
    return sel
}


// ── Badge ─────────────────────────────────────────────────────

const Badge = ({item, onRemove}: {item: ComboItem; onRemove: () => void}) => {
    const hex = item.color || "#7c5cbf"
    const el: HTMLElement = (
        <span
            className="st-badge"
            style={{
                backgroundColor: `${hex}33`,
                color: hex,
                border: `1px solid ${hex}55`,
            }}>
            {item.name}
            <span className="st-badge-x">×</span>
        </span>
    )
    el.querySelector(".st-badge-x")!.addEventListener("click", onRemove)
    return el
}

// ── SongPicker — type a name, pick a song, store the ID ───────
// Songs are fetched once and cached for the lifetime of the page.

type SongOption = {id: string; title: string; artist?: string}

let _songCache: SongOption[] | null = null
let _songFetching = false
const _songWaiters: Array<(s: SongOption[]) => void> = []

const loadSongs = (cb: (songs: SongOption[]) => void): void => {
    if (_songCache) { cb(_songCache); return }
    _songWaiters.push(cb)
    if (_songFetching) return
    _songFetching = true
    STApi.getSongs()
        .then(songs => {
            _songCache = songs.map(s => ({id: s.id, title: s.title, artist: s.artist}))
            _songFetching = false
            _songWaiters.splice(0).forEach(fn => fn(_songCache!))
        })
        .catch(() => {
            _songCache = []
            _songFetching = false
            _songWaiters.splice(0).forEach(fn => fn([]))
        })
}

const SongPicker = ({
    placeholder,
    initialId,
    initialName,
    onSelect,
}: {
    placeholder: string
    initialId: string | undefined
    initialName: string | undefined
    onSelect: (id: string, name: string) => void
}) => {
    let songs: SongOption[] = _songCache ?? []
    let query = ""
    let dropdownOpen = false

    const input: HTMLInputElement = (
        <input className="st-input" type="text" placeholder={placeholder}/>
    )
    const dropdown: HTMLElement = <div className="st-combo-dropdown" style={{display: "none"}}/>
    const searchWrap: HTMLElement = (
        <div className="st-combo-wrap">
            {input}
            {dropdown}
        </div>
    )
    const badgeRow: HTMLElement = <div className="st-badge-row"/>
    const wrap: HTMLElement = <div>{badgeRow}{searchWrap}</div>

    const showSearch = () => {
        badgeRow.style.display = "none"
        searchWrap.style.display = "block"
        input.value = ""
        query = ""
    }

    const showBadge = (name: string, id: string) => {
        searchWrap.style.display = "none"
        badgeRow.innerHTML = ""
        badgeRow.style.display = "block"
        badgeRow.appendChild(Badge({
            item: {id, name, color: "#5c8cbf"},
            onRemove: () => {
                onSelect("", "")
                showSearch()
            },
        }))
    }

    // Restore badge if already selected
    if (initialId && initialName) {
        showBadge(initialName, initialId)
    } else {
        badgeRow.style.display = "none"
    }

    const buildDropdown = () => {
        dropdown.innerHTML = ""
        const q = query.toLowerCase()
        const filtered = q
            ? songs.filter(s =>
                s.title.toLowerCase().includes(q) ||
                (s.artist ?? "").toLowerCase().includes(q))
            : songs.slice(0, 8)

        if (filtered.length === 0) {
            const msg: HTMLElement = (
                <div className="st-combo-option" style={{opacity: "0.5", pointerEvents: "none"}}>
                    {songs.length === 0 ? "Loading…" : "No results"}
                </div>
            )
            dropdown.appendChild(msg)
        } else {
            for (const song of filtered) {
                const label = song.artist ? `${song.title} — ${song.artist}` : song.title
                const opt: HTMLElement = <div className="st-combo-option">{label}</div>
                opt.addEventListener("click", () => {
                    onSelect(song.id, song.title)
                    showBadge(song.title, song.id)
                    hideDropdown()
                })
                dropdown.appendChild(opt)
            }
        }
    }

    const openDropdown = () => {
        dropdownOpen = true
        dropdown.style.display = "block"
        buildDropdown()
        if (songs.length === 0) {
            loadSongs(loaded => {
                songs = loaded
                if (dropdownOpen) buildDropdown()
            })
        }
    }

    const hideDropdown = () => {
        dropdownOpen = false
        dropdown.style.display = "none"
    }

    input.addEventListener("focus", openDropdown)
    input.addEventListener("input", () => {
        query = input.value
        buildDropdown()
        if (!dropdownOpen) openDropdown()
    })
    input.addEventListener("blur", () => setTimeout(hideDropdown, 150))

    return wrap
}

// ── Main view ─────────────────────────────────────────────────

export const SongInfoView = ({service}: Construct) => {
    if (!service.hasProfile) {
        return (
            <div className="st-section st-empty">
                <div>No project open.</div>
                <div>Open a project to edit its song info.</div>
            </div>
        )
    }

    // Gate: this panel only edits song/preacher/loop projects. If the
    // project is a Loop Builder, show a one-click switch instead so
    // both panels never edit the same file at once.
    const storedType = (service.profile.meta as Record<string, unknown>).st_playerType
    if (storedType === "loopBuilder") {
        const switchBtn: HTMLElement = (
            <button type="button" className="st-btn primary" style={{marginTop: "12px"}}>
                Switch this project to Song
            </button>
        )
        switchBtn.addEventListener("click", () => {
            ;(service.profile.meta as Record<string, unknown>).st_playerType = "song"
            service.profile.updateModifyDate()
            document.dispatchEvent(new CustomEvent("st:playertype", {detail: "song"}))
        })
        return (
            <div className="st-section st-empty">
                <div>This project is a Loop Builder.</div>
                <div>Open Loop Builder Info to edit it, or switch the project type.</div>
                {switchBtn}
            </div>
        )
    }

    const st = readST(service)
    const {meta} = service.profile

    // ── Name field ────────────────────────────────────────────

    const inputName: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Song title" value={meta.name ?? ""}/>
    )
    inputName.addEventListener("blur", () => service.profile.updateMetaData("name", inputName.value))

    // ── Artist field ──────────────────────────────────────────

    const inputArtist: HTMLInputElement = (
        <input className="st-input" type="text" placeholder="Artist name" value={meta.artist ?? ""}/>
    )
    inputArtist.addEventListener("blur", () => service.profile.updateMetaData("artist", inputArtist.value))

    // ── Type picker ───────────────────────────────────────────

    let currentType: STType | undefined = st.st_type
    const typeDisplay: HTMLElement = <div className="st-badge-row"/>

    const refreshTypeDisplay = () => {
        typeDisplay.innerHTML = ""
        if (currentType) {
            typeDisplay.appendChild(
                Badge({
                    item: currentType,
                    onRemove: () => {
                        currentType = undefined
                        writeST(service, {st_type: undefined})
                        refreshTypeDisplay()
                    },
                })
            )
        }
    }
    refreshTypeDisplay()

    const typeCombo = Combobox({
        placeholder: "Search or create type…",
        items: STApi.getTypes,
        onSelect: (item) => {
            currentType = item as STType
            writeST(service, {st_type: currentType})
            refreshTypeDisplay()
        },
        onCreate: (name) => {
            const t = STApi.createType(name, "#7c5cbf")
            return t
        },
    })

    // ── Categories picker ─────────────────────────────────────

    let currentCats: STCategory[] = [...(st.st_categories ?? [])]
    const catDisplay: HTMLElement = <div className="st-badge-row"/>

    const refreshCatDisplay = () => {
        catDisplay.innerHTML = ""
        for (const cat of currentCats) {
            catDisplay.appendChild(
                Badge({
                    item: cat,
                    onRemove: () => {
                        currentCats = currentCats.filter(c => c.id !== cat.id)
                        writeST(service, {st_categories: currentCats})
                        refreshCatDisplay()
                    },
                })
            )
        }
    }
    refreshCatDisplay()

    const catCombo = Combobox({
        placeholder: "Search or create category…",
        items: STApi.getCategories,
        onSelect: (item) => {
            if (!currentCats.find(c => c.id === item.id)) {
                currentCats = [...currentCats, item as STCategory]
                writeST(service, {st_categories: currentCats})
                refreshCatDisplay()
            }
        },
        onCreate: (name) => STApi.createCategory(name, "#5cb8bf"),
    })

    // ── Toggles ───────────────────────────────────────────────

    let isPremium     = st.st_is_premium    ?? false
    let isPreviewable = st.st_isPreviewable ?? false
    let isHidden      = st.st_isHidden      ?? false
    let isPreachModeFeatured = st.st_isPreachModeFeatured ?? false

    const premiumToggle = Toggle({
        value: isPremium,
        onChange: (v) => { isPremium = v; writeST(service, {st_is_premium: v}) },
    })
    const previewToggle = Toggle({
        value: isPreviewable,
        onChange: (v) => { isPreviewable = v; writeST(service, {st_isPreviewable: v}) },
    })
    const hiddenToggle = Toggle({
        value: isHidden,
        onChange: (v) => { isHidden = v; writeST(service, {st_isHidden: v}) },
    })
    const featuredToggle = Toggle({
        value: isPreachModeFeatured,
        onChange: (v) => { isPreachModeFeatured = v; writeST(service, {st_isPreachModeFeatured: v}) },
    })

    // ── Key dropdown ──────────────────────────────────────────

    const keySelect = KeySelect({
        value: st.st_key,
        onChange: (v) => writeST(service, {st_key: v})
    })

    // ── OctaveUp picker ───────────────────────────────────────

    const octaveUpSelect = KeyArraySelect({
        value: st.st_octaveUp ?? [],
        onChange: (v) => writeST(service, {st_octaveUp: v})
    })

    // ── OctaveDown picker ─────────────────────────────────────

    const octaveDownSelect = KeyArraySelect({
        value: st.st_octaveDown ?? [],
        onChange: (v) => writeST(service, {st_octaveDown: v})
    })

    // ── Tags input ────────────────────────────────────────────

    const tagsInput: HTMLInputElement = (
        <input className="st-input" type="text"
               placeholder="Tags (comma separated)"
               value={(st.st_tags ?? []).join(", ")}/>
    )
    tagsInput.addEventListener("blur", () => {
        const tags = tagsInput.value.split(",").map(t => t.trim()).filter(Boolean)
        writeST(service, {st_tags: tags})
    })

    // ── PlayerType dropdown ───────────────────────────────────
    // Loop Builder is no longer a player type here — it lives in
    // its own panel. The gate above guarantees we only reach this
    // code for song/preacher/loop projects.

    let currentPlayerType: "song" | "preacher" | "loop" =
        (st.st_playerType === "song" || st.st_playerType === "preacher" || st.st_playerType === "loop")
            ? st.st_playerType
            : "song"
    const isKeyless = () => currentPlayerType === "loop"

    const playerTypeSelect: HTMLSelectElement = (
        <select className="st-input">
            <option value="song">Song</option>
            <option value="preacher">Preacher</option>
            <option value="loop">Loop</option>
        </select>
    )
    playerTypeSelect.value = currentPlayerType
    playerTypeSelect.addEventListener("change", () => {
        currentPlayerType = (playerTypeSelect.value || "song") as "song" | "preacher" | "loop"
        writeST(service, {st_playerType: currentPlayerType as STProjectMeta["st_playerType"]})
        document.dispatchEvent(new CustomEvent("st:playertype", {detail: currentPlayerType}))
        preacherFields.style.display  = currentPlayerType === "preacher" ? "block" : "none"
        songOnlySection.style.display = isKeyless() ? "none" : "block"
        if (isKeyless()) writeST(service, {st_key: 0})
    })

    // ── PreacherCompanion song pickers (conditional) ──────────
    // Always read fresh from meta so concurrent picker updates don't clobber each other

    const readPC = (): NonNullable<STProjectMeta["st_preacherCompanion"]> =>
        ((service.profile.meta as STProjectMeta & Record<string, unknown>)
            .st_preacherCompanion as STProjectMeta["st_preacherCompanion"]) ?? {}

    const pc = st.st_preacherCompanion ?? {}

    const prayerPicker = SongPicker({
        placeholder: "Search for prayer song…",
        initialId:   pc.prayerId,
        initialName: pc.prayerName,
        onSelect: (id, name) => writeST(service, {
            st_preacherCompanion: {
                ...readPC(),
                prayerId:   id   || undefined,
                prayerName: name || undefined,
            },
        }),
    })

    const praiseBreakPicker = SongPicker({
        placeholder: "Search for praise break song…",
        initialId:   pc.praiseBreakId,
        initialName: pc.praiseBreakName,
        onSelect: (id, name) => writeST(service, {
            st_preacherCompanion: {
                ...readPC(),
                praiseBreakId:   id   || undefined,
                praiseBreakName: name || undefined,
            },
        }),
    })

    const talkMusicPicker = SongPicker({
        placeholder: "Search for talk music song…",
        initialId:   pc.talkMusicId,
        initialName: pc.talkMusicName,
        onSelect: (id, name) => writeST(service, {
            st_preacherCompanion: {
                ...readPC(),
                talkMusicId:   id   || undefined,
                talkMusicName: name || undefined,
            },
        }),
    })

    const hypePicker = SongPicker({
        placeholder: "Search for hype song…",
        initialId:   pc.hypeId,
        initialName: pc.hypeName,
        onSelect: (id, name) => writeST(service, {
            st_preacherCompanion: {
                ...readPC(),
                hypeId:   id   || undefined,
                hypeName: name || undefined,
            },
        }),
    })

    const preacherFields: HTMLElement = (
        <div style={{display: currentPlayerType === "preacher" ? "block" : "none"}}>
            <div className="st-toggle-row">
                <span className="st-toggle-label">Set as Default preaching mode</span>
                {featuredToggle}
            </div>
            <div className="st-field">
                <div className="st-label">Prayer</div>
                {prayerPicker}
            </div>
            <div className="st-field">
                <div className="st-label">Praise Break</div>
                {praiseBreakPicker}
            </div>
            <div className="st-field">
                <div className="st-label">Talk Music</div>
                {talkMusicPicker}
            </div>
            <div className="st-field">
                <div className="st-label">Hype</div>
                {hypePicker}
            </div>
        </div>
    )

    // ── Image drop zone ───────────────────────────────────────
    // Drop or click to upload cover art. Shows image preview with
    // a delete button once uploaded. Only the drop zone is shown
    // when no image is set.

    let currentImageUrl:  string | undefined = st.st_image_url
    let currentImageData: string | undefined = st.st_image_data  // base64 stored in project file
    let imageUploading = false

    // Resize image to max 400px and return as JPEG base64 data URL.
    // This small copy lives inside the project file so the DAW can
    // always show it without a network request.
    const resizeToDataUrl = (file: File, maxPx = 400): Promise<string> =>
        new Promise((resolve, reject) => {
            const img = new Image()
            const blobUrl = URL.createObjectURL(file)
            img.onload = () => {
                const scale = Math.min(1, maxPx / Math.max(img.width, img.height))
                const canvas = document.createElement("canvas")
                canvas.width  = Math.round(img.width  * scale)
                canvas.height = Math.round(img.height * scale)
                canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height)
                URL.revokeObjectURL(blobUrl)
                resolve(canvas.toDataURL("image/jpeg", 0.82))
            }
            img.onerror = () => { URL.revokeObjectURL(blobUrl); reject(new Error("Image decode failed")) }
            img.src = blobUrl
        })

    const imageDropZone: HTMLElement = <div className="st-img-drop-zone"/>

    const refreshZone = () => {
        imageDropZone.innerHTML = ""

        if (currentImageData || currentImageUrl) {
            // ── Has image ─────────────────────────────────────
            imageDropZone.classList.add("has-image")

            // Prefer local data URL (always works); fall back to remote URL
            const src = currentImageData ?? currentImageUrl!
            const img: HTMLImageElement = <img src={src} className="st-img-actual" alt="Cover"/>
            imageDropZone.appendChild(img)

            const deleteBtn: HTMLElement = (
                <button type="button" className="st-img-delete-btn" title="Remove image">×</button>
            )
            deleteBtn.addEventListener("click", (e) => {
                e.stopPropagation()
                currentImageUrl  = undefined
                currentImageData = undefined
                writeST(service, {st_image_url: undefined, st_image_data: undefined})
                refreshZone()
            })
            imageDropZone.appendChild(deleteBtn)

        } else if (imageUploading) {
            // ── Uploading ─────────────────────────────────────
            imageDropZone.classList.remove("has-image")
            const lbl: HTMLElement = <div className="st-img-label" style={{color: "var(--st-accent-bright)"}}>Uploading…</div>
            imageDropZone.appendChild(lbl)

        } else {
            // ── Empty drop target ─────────────────────────────
            imageDropZone.classList.remove("has-image")
            const lbl: HTMLElement = (
                <div className="st-img-label">
                    Drop cover image here<br/>
                    <span style={{opacity: "0.5", fontSize: "0.55rem"}}>or click to browse</span>
                </div>
            )
            imageDropZone.appendChild(lbl)
        }
    }
    refreshZone()

    const uploadImageFile = async (file: File) => {
        if (!file.type.startsWith("image/")) return
        if (imageUploading) return
        imageUploading = true
        refreshZone()

        const ext = file.name.split(".").pop() ?? "jpg"
        const key = `${crypto.randomUUID()}.${ext}`
        const uploadUrl = `${CloudflareConfig.WORKER_URL.replace("/daw/projects", "/daw/audio")}/image-upload?key=${encodeURIComponent(key)}`

        try {
            // Resize → store in project file for DAW preview (no network needed on reopen)
            currentImageData = await resizeToDataUrl(file)
            writeST(service, {st_image_data: currentImageData})
            refreshZone() // show preview immediately while upload continues

            // Upload original full-quality file to R2
            const token = await ClerkAuth.getToken()
            const res = await fetch(uploadUrl, {
                method: "PUT",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": file.type,
                },
                body: await file.arrayBuffer(),
            })
            if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
            const data = await res.json() as {image_url: string}
            currentImageUrl = data.image_url
            writeST(service, {st_image_url: currentImageUrl})
        } catch {
            currentImageData = undefined
            writeST(service, {st_image_data: undefined})
            imageDropZone.innerHTML = ""
            const errLabel: HTMLElement = <div className="st-img-label" style={{color: "#e07c3a"}}>Upload failed — try again</div>
            imageDropZone.appendChild(errLabel)
            setTimeout(refreshZone, 2500)
            imageUploading = false
            return
        }

        imageUploading = false
        refreshZone()
    }

    // Hidden file input for click-to-browse
    const fileInput: HTMLInputElement = <input type="file" accept="image/*" style={{display: "none"}}/>
    fileInput.addEventListener("change", () => {
        const file = fileInput.files?.[0]
        if (file) uploadImageFile(file)
        fileInput.value = ""
    })
    document.body.appendChild(fileInput)

    imageDropZone.addEventListener("click", () => { if (!currentImageUrl) fileInput.click() })
    imageDropZone.addEventListener("dragover", (e) => { e.preventDefault(); imageDropZone.classList.add("drag-over") })
    imageDropZone.addEventListener("dragleave", () => imageDropZone.classList.remove("drag-over"))
    imageDropZone.addEventListener("drop", (e) => {
        e.preventDefault()
        imageDropZone.classList.remove("drag-over")
        const file = (e as DragEvent).dataTransfer?.files[0]
        if (file) uploadImageFile(file)
    })

    // ── Status pill ───────────────────────────────────────────

    const statusPill = StatusPill({
        value: st.st_status ?? "draft",
        onChange: (v) => writeST(service, {st_status: v})
    })

    // ── Sync status display ───────────────────────────────────

    const dbId = st.st_db_id
    const syncStatus: HTMLElement = dbId
        ? <div style={{fontSize: "0.6rem", color: "#80c080"}}>✓ Synced to library (ID: {dbId})</div>
        : <div style={{fontSize: "0.6rem", color: "var(--color-dark)"}}>Not yet published to library</div>

    // ── Render ────────────────────────────────────────────────

    const artistField: HTMLElement = (
        <div className="st-field">
            <div className="st-label">Artist</div>
            {inputArtist}
        </div>
    )

    const typeField: HTMLElement = (
        <div className="st-field">
            <div className="st-label">Type</div>
            {typeDisplay}
            {typeCombo}
        </div>
    )

    // song-only section: Key, Octave Up/Down — hidden for the keyless "loop" type
    const songOnlySection: HTMLElement = (
        <div style={{display: isKeyless() ? "none" : "block"}}>
            <hr className="st-divider"/>
            <div className="st-field">
                <div className="st-label">Key *</div>
                {keySelect}
            </div>
            <div className="st-field">
                <div className="st-label">Octave Up</div>
                {octaveUpSelect}
            </div>
            <div className="st-field">
                <div className="st-label">Octave Down</div>
                {octaveDownSelect}
            </div>
        </div>
    )

    return (
        <div className="st-section">
            {statusPill}
            <div className="st-field">
                <div className="st-label">Title</div>
                {inputName}
            </div>

            <div className="st-toggle-row">
                <span className="st-toggle-label">Hide from library screen</span>
                {hiddenToggle}
            </div>

            {artistField}

            <div className="st-field">
                <div className="st-label">Player Type</div>
                {playerTypeSelect}
            </div>

            {preacherFields}

            <hr className="st-divider"/>

            {typeField}

            <div className="st-field">
                <div className="st-label">Categories</div>
                {catDisplay}
                {catCombo}
            </div>

            {songOnlySection}

            <hr className="st-divider"/>

            <div className="st-field">
                <div className="st-label">Tags</div>
                {tagsInput}
            </div>

            <div className="st-field">
                <div className="st-label">Cover Image</div>
                {imageDropZone}
            </div>

            <hr className="st-divider"/>

            <div className="st-toggle-row">
                <span className="st-toggle-label">Premium</span>
                {premiumToggle}
            </div>

            <div className="st-toggle-row">
                <span className="st-toggle-label">Previewable</span>
                {previewToggle}
            </div>

            <hr className="st-divider"/>

            {syncStatus}
        </div>
    )
}
